/**
 * Inputs for the D2C self-funding sequence, shared by its four steps.
 *
 * Everything comes from the consumer's own data: the client record (Redux, with its live funding
 * sources), the consumer plan (which keeps the picked funding-source types so a refresh resumes where
 * the consumer was), and the funding-source save state. Nothing reads a guided-planning session.
 */
import { useMemo } from 'react';
import {
  ASSET_TYPE_TO_ACCOUNT_TYPE,
  getAssetTypeIdFromAccountType,
  useFundingAssessment,
  useFundingSourcesState,
  type AssetTypeId,
} from '@/features/funding-sources';
import { useConsumerClient } from './use-consumer-client';
import { useConsumerPlan } from './use-consumer-plan';

function typesFromExistingSources(
  selfFunding: Record<string, SelfFundingSource> | undefined,
): AssetTypeId[] {
  const types = new Set<AssetTypeId>();
  Object.values(selfFunding ?? {}).forEach(source => {
    const typeId = getAssetTypeIdFromAccountType(source.accountType);
    if (typeId && typeId in ASSET_TYPE_TO_ACCOUNT_TYPE) types.add(typeId);
  });
  return [...types];
}

export function useSelfFundingSequence(clientId: string) {
  const {
    client,
    status: clientStatus,
    isLoading: isClientLoading,
    retry: retryClient,
  } = useConsumerClient(clientId);
  const {
    plan,
    isLoading: isPlanLoading,
    savePlan,
    isSaving: isPlanSaving,
  } = useConsumerPlan(clientId);
  const { isSaving: isSourceSaving, saveError } = useFundingSourcesState();

  const fundingSources = client?.multipleFundingSources;
  const savedTypes = plan?.fundingSourceTypes ?? null;
  const selectedTypes = useMemo(
    () => savedTypes ?? typesFromExistingSources(fundingSources?.selfFunding),
    [savedTypes, fundingSources?.selfFunding],
  );

  const assessment = useFundingAssessment({
    client,
    clientStatus,
    retryClient,
    requireSelfFundingAccount: true,
    includeRecordedPolicies: true,
    combinedCoverageRule: 'engine',
  });

  return {
    fundingSources,
    savedTypes,
    selectedTypes,
    isLoading: isClientLoading || isPlanLoading,
    isPlanSaving,
    isSourceSaving,
    saveError,
    assessment,
    saveTypes: (types: AssetTypeId[]) => savePlan({ fundingSourceTypes: types }),
  };
}
